import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SendOfferModal } from "../../../otc-requests/components/SendOfferModal";
import { EscalateModal } from "../../../otc-requests/components/EscalateModal";
import { RejectModal } from "../../../otc-requests/components/RejectModal";
import { ApproveRequestModal } from "../../../otc-requests/components/ApproveRequestModal";
import { UpdateStatusModal } from "../../../otc-requests/components/UpdateStatusModal";
import { CancelOfferModal } from "../../../otc-requests/components/CancelOfferModal";
import { OtcRequestTopSection } from "../../../otc-requests/components/OtcRequestTopSection";
import {
  AuthenticationRequiredModal,
  CompleteTransferChoiceModal,
  CompleteTransferMethod,
  DeclineTransferModal,
  RecordTransferManualModal,
  SendMoneyWizardModal,
  TransferSubmittedSuccessModal,
} from "../../../otc-requests/components/complete-transfer";
import { TransactionAttachmentsCard } from "./TransactionAttachmentsCard";
import { TransactionActivityLogCard } from "./TransactionActivityLogCard";
import { OtcOverviewCard } from "./OtcOverviewCard";
import { FlagTransactionModal } from "./FlagTransactionModal";
import { TransactionAlertBanner } from "./TransactionAlertBanner";
import { ReportDetailsFlyin } from "./ReportDetailsFlyin";
import { TransactionItem, TransactionReportData } from "../../types";
import { useOtcDetailsState } from "./useOtcDetailsState";
import { downloadTransactionReceipt } from "../../services/downloadTransactionReceipt";
import { flagTransaction } from "../../services/flagTransaction";

export interface OtcDetailsViewProps {
  transaction: TransactionItem;
  id?: string;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onNavigateSibling?: (delta: number) => void;
}

export const OtcDetailsView: React.FC<OtcDetailsViewProps> = ({
  transaction,
  id,
  canGoPrevious: propCanGoPrevious,
  canGoNext: propCanGoNext,
  onNavigateSibling: propOnNavigateSibling,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: trnxId } = useParams()
  const {
    currentRequest,
    activityLogs,
    canGoPrevious: internalCanGoPrevious,
    canGoNext: internalCanGoNext,
    handleNavigateSibling: internalNavigateSibling,
    handleBack,
    isSendOfferOpen,
    setIsSendOfferOpen,
    isEscalateOpen,
    setIsEscalateOpen,
    isRejectOpen,
    setIsRejectOpen,
    isApproveOpen,
    setIsApproveOpen,
    isUpdateStatusOpen,
    setIsUpdateStatusOpen,
    isCancelOfferOpen,
    setIsCancelOfferOpen,
    handleSendOfferSubmit,
    handleApproveConfirm,
    handleCancelOffer,
    handleUpdateStatusConfirm,
    handleEscalateConfirm,
    handleRejectConfirm,
    handleUpdateTransactionStatus,
    isActionSubmitting,
  } = useOtcDetailsState(transaction, id);

  const canGoPrevious = propCanGoPrevious ?? internalCanGoPrevious;
  const canGoNext = propCanGoNext ?? internalCanGoNext;
  const handleNavigateSibling = propOnNavigateSibling ?? internalNavigateSibling;

  const isFlagged = Boolean(
    transaction.isFlagged ||
      transaction.status?.toLowerCase() === "flagged" ||
      currentRequest.state === "Flagged"
  );

  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const [isReportFlyinOpen, setIsReportFlyinOpen] = useState(false);
  const [reportData, setReportData] = useState<TransactionReportData | null>(null);

  useEffect(() => {
    if (isFlagged && !reportData) {
      const issueText =
        (transaction as any).issue ||
        (transaction as any).reason ||
        currentRequest.notes ||
        "";
      setReportData({
        reason: issueText,
        description: issueText,
        dateReported: currentRequest.date || "Recently",
        reportedBy: "Admin",
        timeline: [
          {
            id: `tl-flag-${Date.now()}`,
            timestamp: currentRequest.date || "Recently",
            title: `Transaction flagged (${issueText})`,
            actorName: "Admin",
            statusDotColor: "amber",
          },
        ],
      });
    }
  }, [isFlagged, transaction, currentRequest.notes, currentRequest.date, reportData]);

  // Complete Transfer flow modals
  const [isCompleteChoiceOpen, setIsCompleteChoiceOpen] = useState(false);
  const [isSendWizardOpen, setIsSendWizardOpen] = useState(false);
  const [isManualTransferOpen, setIsManualTransferOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const handleFlagSubmit = async (data: {
    issueReason: string;
    escalateType: "role" | "member";
    escalateTargetName: string;
    roleId?: string;
    adminId?: string;
    note: string;
  }) => {
    const targetId = transaction?.id || transaction?._id || id || trnxId;
    const roleIdToPass = data.roleId || data.adminId || "";

    if (targetId) {
      try {
        await flagTransaction({
          transactionId: targetId,
          status: "Flagged",
          issue: data.issueReason,
          role: roleIdToPass,
        });

        // Invalidate react-query cache so all transaction views and lists refresh
        await queryClient.invalidateQueries({
          queryKey: ["admin-transaction-details"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["transactions"],
        });

        setReportData({
          reason: data.issueReason,
          description: data.note || "Transaction reported by admin for review.",
          dateReported: "Just now",
          reportedBy: "Admin",
          escalatedTo: { type: data.escalateType, name: data.escalateTargetName },
          timeline: [
            {
              id: `tl-${Date.now()}-1`,
              timestamp: "Just now",
              title: `Issue escalated to ${data.escalateTargetName}`,
              actorName: "Admin",
              statusDotColor: "amber",
            },
            {
              id: `tl-${Date.now()}-2`,
              timestamp: "Just now",
              title: "Transaction reported",
              actorName: "Admin",
              statusDotColor: "green",
            },
          ],
        });

        toast.success("Transaction flagged successfully");
      } catch (err: unknown) {
        console.warn("Flag transaction request failed:", err);
        const axiosErr = err as {
          response?: { data?: { message?: string | string[] } };
          message?: string;
        };
        const msg =
          axiosErr?.response?.data?.message ||
          axiosErr?.message ||
          "Failed to flag transaction";
        toast.error(Array.isArray(msg) ? msg.join(", ") : String(msg));
      }
    }
  };

  const handleChoiceContinue = (method: CompleteTransferMethod) => {
    setIsCompleteChoiceOpen(false);
    if (method === "direct") {
      setIsSendWizardOpen(true);
    } else {
      setIsManualTransferOpen(true);
    }
  };

  const handleWizardSuccess = () => {
    setIsSendWizardOpen(false);
    setIsSuccessModalOpen(true);
  };

  const handleManualConfirm = (data: { proofFile: File | null; note: string }) => {
    setIsManualTransferOpen(false);
    handleUpdateTransactionStatus(
      data.proofFile?.name
        ? {
            status: "Success",
            url: `https://storage.tradevu.com/proofs/${data.proofFile.name}`,
          }
        : { status: "Success" }
    );
    toast.success("Transfer recorded manually and status marked as Completed");
  };

  const handleAuthConfirm = (code: string) => {
    setIsAuthModalOpen(false);
    handleUpdateTransactionStatus({
      status: "Success",
    });
    toast.success("Transfer approved and marked as Completed!");
  };

  const handleDeclineConfirm = (reason: string) => {
    setIsDeclineModalOpen(false);
    handleUpdateTransactionStatus({
      status: "Failed",
      reason: reason || "Settlement could not be completed.",
    });
    handleRejectConfirm(reason);
  };

  return (
    <section className="min-h-full bg-[#F3F4F8] md:p-6">
      <div className="rounded-[16px] bg-white p-4 md:p-6">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
          <OtcRequestTopSection
            request={currentRequest}
            isAdminExternalPayout={Boolean(
              transaction.isAdminExternalPayout ??
                (transaction.otc as any)?.isAdminExternalPayout ??
                currentRequest.isAdminExternalPayout
            )}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            onNavigateSibling={handleNavigateSibling}
            onBack={handleBack}
            onReject={() => setIsRejectOpen(true)}
            onSendOffer={() => setIsSendOfferOpen(true)}
            onApprove={() => setIsApproveOpen(true)}
            onCancelOffer={() => setIsCancelOfferOpen(true)}
            onUpdateStatus={() => setIsUpdateStatusOpen(true)}
            onEscalate={() => setIsEscalateOpen(true)}
            onFlagRequest={() => setIsFlagModalOpen(true)}
            onCompleteTransfer={() => {
              const targetId = trnxId || transaction.id || transaction._id || currentRequest.transactionId || currentRequest.id;
              const otcId =
                transaction.otc?._id ||
                transaction.otc?.id ||
                (transaction.otc as any)?.otcId ||
                currentRequest.otcId;
              console.log({
                request: currentRequest,
                transaction,
                otcId,
              })
              navigate(`/v2/transactions/${targetId}/complete-transfer`, {
                state: {
                  request: currentRequest,
                  transaction,
                  otcId,
                },
              });
            }}
            onDeclineTransfer={() => setIsDeclineModalOpen(true)}
            onApproveTransfer={() => setIsAuthModalOpen(true)}
          />

          {isFlagged && (
            <TransactionAlertBanner
              isFlagged={isFlagged}
              isResolved={false}
              reportData={reportData}
              onReviewReport={() => setIsReportFlyinOpen(true)}
            />
          )}

          <OtcOverviewCard
            request={currentRequest}
            onRefreshStatus={() => toast.info("Status is up to date")}
            onViewWallet={() => toast.info("Opening customer wallet ledger")}
          />

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="flex flex-col gap-3">
              <h2 className="text-[16px] font-semibold text-[#101828]">Attachments</h2>
              <TransactionAttachmentsCard transaction={transaction} />
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-[16px] font-semibold text-[#101828]">Activity Log</h2>
              <TransactionActivityLogCard events={activityLogs} />
            </div>
          </div>
        </div>
      </div>

      {/* Complete Transfer Modal Flow */}
      <CompleteTransferChoiceModal
        open={isCompleteChoiceOpen}
        onClose={() => setIsCompleteChoiceOpen(false)}
        onContinue={handleChoiceContinue}
      />

      <SendMoneyWizardModal
        open={isSendWizardOpen}
        request={currentRequest}
        onClose={() => setIsSendWizardOpen(false)}
        onSubmitSuccess={handleWizardSuccess}
      />

      <RecordTransferManualModal
        open={isManualTransferOpen}
        onClose={() => setIsManualTransferOpen(false)}
        onConfirm={handleManualConfirm}
      />

      <TransferSubmittedSuccessModal
        open={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        onDownloadReceipt={() => {
          const targetTrxId =
            trnxId ||
            transaction.id ||
            transaction._id ||
            currentRequest.transactionId ||
            currentRequest.id;
          if (targetTrxId) {
            downloadTransactionReceipt(targetTrxId);
          }
        }}
      />

      <AuthenticationRequiredModal
        open={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onConfirm={handleAuthConfirm}
      />

      <DeclineTransferModal
        open={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        onConfirm={handleDeclineConfirm}
      />

      {/* Secondary & existing action modals */}
      <SendOfferModal
        open={isSendOfferOpen}
        request={currentRequest}
        isSubmitting={isActionSubmitting}
        onClose={() => setIsSendOfferOpen(false)}
        onSubmit={handleSendOfferSubmit}
      />

      <ApproveRequestModal
        open={isApproveOpen}
        request={currentRequest}
        isSubmitting={isActionSubmitting}
        onClose={() => setIsApproveOpen(false)}
        onConfirm={handleApproveConfirm}
        onSendCounterOffer={() => {
          setIsApproveOpen(false);
          setIsSendOfferOpen(true);
        }}
      />

      <EscalateModal
        open={isEscalateOpen}
        customerName={currentRequest.customer.name}
        isSubmitting={isActionSubmitting}
        onClose={() => setIsEscalateOpen(false)}
        onConfirm={handleEscalateConfirm}
      />

      <FlagTransactionModal
        open={isFlagModalOpen}
        onOpenChange={setIsFlagModalOpen}
        onSubmit={handleFlagSubmit}
      />

      <ReportDetailsFlyin
        open={isReportFlyinOpen}
        onOpenChange={setIsReportFlyinOpen}
        reportData={reportData}
        activityLogs={activityLogs}
      />

      <RejectModal
        open={isRejectOpen}
        customerName={currentRequest.customer.name}
        isSubmitting={isActionSubmitting}
        onClose={() => setIsRejectOpen(false)}
        onConfirm={handleRejectConfirm}
      />

      <UpdateStatusModal
        open={isUpdateStatusOpen}
        currentStatus={currentRequest.state}
        isSubmitting={isActionSubmitting}
        onClose={() => setIsUpdateStatusOpen(false)}
        onConfirm={handleUpdateStatusConfirm}
      />

      <CancelOfferModal
        open={isCancelOfferOpen}
        isSubmitting={isActionSubmitting}
        onClose={() => setIsCancelOfferOpen(false)}
        onConfirm={handleCancelOffer}
      />
    </section>
  );
};
